import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { delinkRecipient } from '../../../pages/Billing/Invoice/newInvoice/newInvoiceForm.helper';
import { useAlert } from '../../alertProvider';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface DelinkRecipientDialogProps {
  open: boolean;
  invoiceId: number | null;
  onClose: () => void;
  onSuccess?: () => void;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const DIALOG_PAPER_SX = {
  borderRadius: '20px',
  width: 460,
  maxWidth: '95vw',
  p: 0,
  overflow: 'visible',
  boxShadow: '0px 12px 48px rgba(0, 0, 0, 0.14)',
};

const CONFIRM_BTN_SX = {
  textTransform: 'none' as const,
  fontWeight: 700,
  fontSize: '0.875rem',
  py: 1.3,
  borderRadius: '10px',
  backgroundColor: '#200d0d',
  color: '#fff',
  boxShadow: 'none',
  '&:hover': { backgroundColor: '#B71C1C', boxShadow: 'none' },
  '&.Mui-disabled': { backgroundColor: '#E0E0E0', color: '#9E9E9E' },
};

const CANCEL_BTN_SX = {
  textTransform: 'none' as const,
  fontWeight: 700,
  fontSize: '0.875rem',
  py: 1.3,
  borderRadius: '10px',
  borderColor: '#bdbdbd',
  color: '#616161',
  '&:hover': { borderColor: '#9e9e9e', backgroundColor: 'rgba(0, 0, 0, 0.04)' },
};

const TITLE_ID = 'delink-recipient-dialog-title';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const DelinkRecipientDialog: React.FC<DelinkRecipientDialogProps> = ({
  open,
  invoiceId,
  onClose,
  onSuccess,
}) => {
  const [reason, setReason] = useState('');
  const queryClient = useQueryClient();
  const { addAlert } = useAlert();

  const resetAndClose = useCallback(() => {
    setReason('');
    onClose();
  }, [onClose]);

  React.useEffect(() => {
    if (open) setReason('');
  }, [open, invoiceId]);

  const { mutate, isPending } = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      delinkRecipient(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      addAlert('Recipient delinked successfully.', 'success');
      resetAndClose();
      onSuccess?.();
    },
    onError: () => {
      addAlert('Failed to delink recipient. Please try again.', 'error');
    },
  });

  const canSubmit = !!reason.trim() && !isPending;

  const handleSave = useCallback(() => {
    if (!invoiceId || !reason.trim()) return;
    mutate({ id: invoiceId, reason: reason.trim() });
  }, [invoiceId, reason, mutate]);

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      PaperProps={{ sx: DIALOG_PAPER_SX }}
      aria-labelledby={TITLE_ID}
      aria-modal="true"
      data-testid="delink-recipient-dialog"
    >
      {/* Close button */}
      <IconButton
        onClick={resetAndClose}
        aria-label="Close"
        size="small"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 1,
          width: 30,
          height: 30,
          backgroundColor: '#f5f5f5',
          '&:hover': { backgroundColor: '#e8e8e8' },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 16, color: '#666' }} />
      </IconButton>

      <DialogContent sx={{ px: 4, pt: 4, pb: 1 }}>
        {/* Icon + Header */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              backgroundColor: '#FFEBEE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <LinkOffRoundedIcon sx={{ fontSize: 30, color: '#D32F2F' }} />
          </Box>
          <Typography
            id={TITLE_ID}
            sx={{ fontWeight: 800, fontSize: '1.15rem', color: '#1a1a1a', mb: 0.75 }}
          >
            Delink Recipient
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#777', textAlign: 'center', lineHeight: 1.6, fontSize: '0.82rem', maxWidth: 340 }}
          >
            This will unlink the recipient from this invoice.
          </Typography>
        </Box>

        {/* Reason input */}
        <TextField
          label="Reason for delinking"
          required
          multiline
          rows={3}
          fullWidth
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Incorrect recipient linked, billing error..."
          slotProps={{ htmlInput: { maxLength: 500 } }}
          data-testid="delink-reason-input"
        />
      </DialogContent>

      <DialogActions sx={{ px: 4, pb: 3.5, pt: 2, gap: 1.5 }}>
        <Button
          variant="outlined"
          fullWidth
          onClick={resetAndClose}
          disabled={isPending}
          sx={CANCEL_BTN_SX}
          data-testid="delink-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          fullWidth
          onClick={handleSave}
          disabled={!canSubmit}
          sx={CONFIRM_BTN_SX}
          data-testid="delink-save-btn"
        >
          {isPending ? <CircularProgress size={20} color="inherit" /> : 'Delink Recipient'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DelinkRecipientDialog;
